// src/components/OfferpackageEdit.tsx

import React, { useState, ChangeEvent, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    IconButton,
    Grid,
    Typography,
    Box,
    Stack,
    Divider,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import DeleteIcon from "@mui/icons-material/Delete";
import { useCreateOfferPackage, useUpdateOfferPackage } from "../../hooks/data/data_offerpackage";
import { useGetServices } from "../../hooks/data/data_service";
import { useGetMaterials, useGetMaterialCategories, useGetMaterialSubcategories } from "../../hooks/data/data_material";
import { IOfferPackage } from "../../interfaces/IOfferpackage";
import { IService } from "../../interfaces/IService";
import { IMaterial } from "../../interfaces/IMaterial";
import { IMaterialCategory } from "../../interfaces/IMaterialCategory";
import { IMaterialSubcategory } from "../../interfaces/IMaterialSubcategory";

interface OfferpackageEditProps {
    open: boolean;
    onClose: () => void;
    initialData: IOfferPackage | null;
    readOnly?: boolean;
}

export const OfferpackageEdit: React.FC<OfferpackageEditProps> = ({ open, onClose, initialData, readOnly = false }) => {
    const { serviceArray } = useGetServices();
    const { materialArray } = useGetMaterials();
    const { materialCategoryArray } = useGetMaterialCategories();
    const { materialSubcategoryArray } = useGetMaterialSubcategories();
    const { createOfferPackage } = useCreateOfferPackage();
    const { updateOfferPackage } = useUpdateOfferPackage();

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [services, setServices] = useState<{ service: number; quantity: number }[]>([]);
    const [materials, setMaterials] = useState<{ material: number; quantity: number }[]>([]);
    const [definitions, setDefinitions] = useState<
        { material_category: number; material_subcategory: number | null; quantity: number }[]
    >([]);

    useEffect(() => {
        if (!open) return;
        if (initialData) {
            setName(initialData.name);
            setDescription(initialData.description);
            setServices(
                initialData.package_services.map((s) => ({
                    service: s.service,
                    quantity: s.quantity,
                })),
            );
            setMaterials(
                initialData.package_materials.map((m) => ({
                    material: m.material,
                    quantity: m.quantity,
                })),
            );
            setDefinitions(
                initialData.package_material_defs.map((d) => ({
                    material_category: d.material_category,
                    material_subcategory: d.material_subcategory,
                    quantity: d.quantity,
                })),
            );
        } else {
            setName("");
            setDescription("");
            setServices([]);
            setMaterials([]);
            setDefinitions([]);
        }
    }, [open, initialData]);

    const addService = () => setServices((prev) => [...prev, { service: 0, quantity: 1 }]);
    const updateService = (idx: number, field: "service" | "quantity", value: number) => {
        const copy = [...services];
        copy[idx][field] = value;
        setServices(copy);
    };
    const removeService = (idx: number) => setServices((prev) => prev.filter((_, i) => i !== idx));

    const addMaterial = () => setMaterials((prev) => [...prev, { material: 0, quantity: 1 }]);
    const updateMaterial = (idx: number, field: "material" | "quantity", value: number) => {
        const copy = [...materials];
        copy[idx][field] = value;
        setMaterials(copy);
    };
    const removeMaterial = (idx: number) => setMaterials((prev) => prev.filter((_, i) => i !== idx));

    const addDefinition = () =>
        setDefinitions((prev) => [...prev, { material_category: 0, material_subcategory: null, quantity: 1 }]);
    const updateDefinition = (
        idx: number,
        field: "material_category" | "material_subcategory" | "quantity",
        value: number | null,
    ) => {
        const copy = [...definitions];
        // @ts-ignore
        copy[idx][field] = value;
        setDefinitions(copy);
    };
    const removeDefinition = (idx: number) => setDefinitions((prev) => prev.filter((_, i) => i !== idx));

    const handleSave = async () => {
        const payload: IOfferPackage = {
            id: initialData?.id ?? 0,
            name,
            description,
            package_services: services.map((s) => ({
                id: s.service,
                service: s.service,
                quantity: s.quantity,
            })),
            package_materials: materials.map((m) => ({
                id: m.material,
                material: m.material,
                quantity: m.quantity,
            })),
            package_material_defs: definitions.map((d) => ({
                id: 0,
                material_category: d.material_category,
                material_subcategory: d.material_subcategory,
                quantity: d.quantity,
            })),
        };

        try {
            if (initialData) {
                await updateOfferPackage({ id: initialData.id, payload });
            } else {
                await createOfferPackage(payload);
            }
            onClose();
        } catch (err) {
            console.error(err);
        }
    };

    const canSave =
        !readOnly &&
        name.trim() !== "" &&
        (services.length > 0 || materials.length > 0 || definitions.length > 0) &&
        services.every((s) => s.service > 0 && s.quantity >= 0) &&
        materials.every((m) => m.material > 0 && m.quantity >= 0) &&
        definitions.every(
            (d) =>
                d.material_category > 0 &&
                d.material_subcategory !== null &&
                d.material_subcategory > 0 &&
                d.quantity >= 0,
        );

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                {readOnly ? "Paket ansehen" : initialData ? "Paket bearbeiten" : "Paket erstellen"}
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={3}>
                    {/* Basisfelder */}
                    <Stack spacing={2}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={readOnly}
                        />
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="Beschreibung"
                            multiline
                            minRows={2}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            disabled={readOnly}
                        />
                    </Stack>

                    <Divider />

                    {/* Leistungen */}
                    <Box>
                        <Typography variant="h6">Leistungen</Typography>
                        <Stack spacing={2}>
                            {services.map((svc, idx) => (
                                <Grid container spacing={2} alignItems="center" key={idx}>
                                    <Grid item xs={7}>
                                        <Autocomplete<IService>
                                            options={serviceArray}
                                            getOptionLabel={(opt) => `${opt.title} (${opt.articelNumber})`}
                                            value={serviceArray.find((s) => s.id === svc.service) ?? null}
                                            onChange={(_, v) => updateService(idx, "service", v?.id ?? 0)}
                                            disabled={readOnly}
                                            renderInput={(params) => <TextField {...params} label="Service" />}
                                        />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <TextField
                                            type="number"
                                            variant="outlined"
                                            label="Menge"
                                            value={svc.quantity}
                                            onChange={(e) => updateService(idx, "quantity", Number(e.target.value))}
                                            inputProps={{ min: 0 }}
                                            disabled={readOnly}
                                            sx={{ maxWidth: 100 }}
                                        />
                                    </Grid>
                                    <Grid item xs={2}>
                                        {!readOnly && (
                                            <IconButton onClick={() => removeService(idx)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </Grid>
                                </Grid>
                            ))}
                            {!readOnly && (
                                <Box textAlign="center">
                                    <Button variant="text" onClick={addService}>
                                        + Leistung hinzufügen
                                    </Button>
                                </Box>
                            )}
                        </Stack>
                    </Box>

                    <Divider />

                    {/* Materialien */}
                    <Box>
                        <Typography variant="h6">Materialien</Typography>
                        <Stack spacing={2}>
                            {materials.map((mat, idx) => (
                                <Grid container spacing={2} alignItems="center" key={idx}>
                                    <Grid item xs={7}>
                                        <Autocomplete<IMaterial>
                                            options={materialArray}
                                            getOptionLabel={(opt) => `${opt.title} (${opt.articelNumber})`}
                                            value={materialArray.find((m) => m.id === mat.material) ?? null}
                                            onChange={(_, v) => updateMaterial(idx, "material", v?.id ?? 0)}
                                            disabled={readOnly}
                                            renderInput={(params) => <TextField {...params} label="Material" />}
                                        />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <TextField
                                            type="number"
                                            variant="outlined"
                                            label="Menge"
                                            value={mat.quantity}
                                            onChange={(e) => updateMaterial(idx, "quantity", Number(e.target.value))}
                                            inputProps={{ min: 0 }}
                                            disabled={readOnly}
                                            sx={{ maxWidth: 100 }}
                                        />
                                    </Grid>
                                    <Grid item xs={2}>
                                        {!readOnly && (
                                            <IconButton onClick={() => removeMaterial(idx)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </Grid>
                                </Grid>
                            ))}
                            {!readOnly && (
                                <Box textAlign="center">
                                    <Button variant="text" onClick={addMaterial}>
                                        + Material hinzufügen
                                    </Button>
                                </Box>
                            )}
                        </Stack>
                    </Box>

                    <Divider />

                    {/* Kategorie / Unterkategorie */}
                    <Box>
                        <Typography variant="h6">Kategorie / Unterkategorie</Typography>
                        <Stack spacing={2}>
                            {definitions.map((def, idx) => (
                                <Grid container spacing={2} alignItems="center" key={idx}>
                                    <Grid item xs={5}>
                                        <Autocomplete<IMaterialCategory>
                                            options={materialCategoryArray}
                                            getOptionLabel={(opt) => opt.materialCategory}
                                            value={
                                                materialCategoryArray.find((c) => c.id === def.material_category) ||
                                                null
                                            }
                                            onChange={(_, v) => {
                                                updateDefinition(idx, "material_category", v?.id ?? 0);
                                                updateDefinition(idx, "material_subcategory", null);
                                            }}
                                            disabled={readOnly}
                                            renderInput={(params) => <TextField {...params} label="Kategorie" />}
                                        />
                                    </Grid>
                                    <Grid item xs={5}>
                                        <Autocomplete<IMaterialSubcategory>
                                            options={materialSubcategoryArray.filter(
                                                (sc) => sc.MaterialCategory === def.material_category,
                                            )}
                                            getOptionLabel={(opt) => opt.materialSubcategory}
                                            value={
                                                materialSubcategoryArray.find(
                                                    (sc) => sc.id === def.material_subcategory,
                                                ) || null
                                            }
                                            onChange={(_, v) =>
                                                updateDefinition(idx, "material_subcategory", v?.id ?? null)
                                            }
                                            disabled={readOnly}
                                            renderInput={(params) => <TextField {...params} label="Unterkategorie" />}
                                        />
                                    </Grid>
                                    <Grid item xs={1}>
                                        <TextField
                                            type="number"
                                            variant="outlined"
                                            label="Anz."
                                            value={def.quantity}
                                            onChange={(e) => updateDefinition(idx, "quantity", Number(e.target.value))}
                                            inputProps={{ min: 0 }}
                                            disabled={readOnly}
                                            sx={{ maxWidth: 80 }}
                                        />
                                    </Grid>
                                    <Grid item xs={1}>
                                        {!readOnly && (
                                            <IconButton onClick={() => removeDefinition(idx)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </Grid>
                                </Grid>
                            ))}
                            {!readOnly && (
                                <Box textAlign="center">
                                    <Button variant="text" onClick={addDefinition}>
                                        + Kategorie hinzufügen
                                    </Button>
                                </Box>
                            )}
                        </Stack>
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose}>{readOnly ? "Schließen" : "Abbrechen"}</Button>
                {!readOnly && (
                    <Button variant="contained" onClick={handleSave} disabled={!canSave}>
                        Speichern
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
